import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Logo } from "@/components/Logo";
import { openCookieSettings } from "@/components/CookieConsent";
import { Button } from "@/components/ui/button";
import {
  ArrowRight, ArrowUpRight, ShieldCheck, Handshake, Sparkles, Building2,
  Quote, Globe, Users, Menu, X,
} from "lucide-react";

const FOUNDER_URL = "https://customer-assets.emergentagent.com/job_fgp-product-showcase/artifacts/0birdyc4_478846080_18490078375053017_7021431969105991145_n..jpg";

const ACCENTS = [
  { bg: "bg-[#EEF5FB]", text: "text-[#2D74B4]" },
  { bg: "bg-[#EDF8F2]", text: "text-[#1F8B53]" },
  { bg: "bg-[#FBF4E2]", text: "text-[#A97A00]" },
  { bg: "bg-[#FBEFF1]", text: "text-[#C13348]" },
  { bg: "bg-[#F4ECF8]", text: "text-[#7E4490]" },
];

const Label = ({ icon: Icon, children, tint = ACCENTS[0] }) => (
  <div className="flex items-center gap-2 mb-4">
    <span className={`h-7 w-7 rounded-lg flex items-center justify-center ${tint.bg} ${tint.text}`}><Icon className="h-3.5 w-3.5" /></span>
    <span className={`text-xs font-semibold uppercase tracking-[0.18em] ${tint.text}`}>{children}</span>
  </div>
);

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.55, delay }} className={className}>
    {children}
  </motion.div>
);

const ecosystem = [
  {
    name: "Desk", url: "https://swishrdesk.com",
    thumb: "https://image.thum.io/get/width/1000/crop/700/noanimate/https://swishrdesk.com",
    desc: "The premier AI-powered business productivity and automation platform used across 30+ countries to instantly generate and manage contracts, proposals and CRM workflows.",
  },
  {
    name: "Pulse", url: "https://swishrpulse.com",
    thumb: "https://image.thum.io/get/width/1000/crop/700/noanimate/https://swishrpulse.com",
    desc: "The advanced contact-centre decision and operational intelligence engine that unifies workforce analytics, AI predictions and quality tracking for large-scale operations.",
  },
  {
    name: "Hire", url: "https://swishrhire.com",
    thumb: "https://image.thum.io/get/width/1000/crop/700/noanimate/https://swishrhire.com",
    desc: "Swishr's elite talent acquisition arm — sourcing and placing high-performing executive assistants, PAs and support professionals for demanding private offices worldwide.",
  },
];

export default function About() {
  const [mobileOpen, setMobileOpen] = useState(false);
  return (
    <div className="min-h-screen bg-white text-neutral-900 font-sans overflow-x-hidden">
      {/* Nav */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-neutral-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-32 flex items-center justify-between">
          <Link to="/"><Logo className="h-20 sm:h-28" /></Link>
          <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-neutral-600">
            <Link to="/" className="hover:text-neutral-900 transition-colors">Home</Link>
            <Link to="/about" className="text-neutral-900">About</Link>
            <Link to="/#features" className="hover:text-neutral-900 transition-colors">Features</Link>
            <Link to="/#pricing" className="hover:text-neutral-900 transition-colors">Pricing</Link>
          </nav>
          <div className="hidden md:flex items-center gap-2">
            <Link to="/login"><Button variant="ghost" data-testid="about-nav-login" className="text-neutral-700 rounded-full">Log in</Button></Link>
            <Link to="/register"><Button data-testid="about-nav-cta" className="rounded-full bg-[#516670] hover:bg-[#41545d] gap-1.5">Get started <ArrowRight className="h-4 w-4" /></Button></Link>
          </div>
          <button onClick={() => setMobileOpen((o) => !o)} data-testid="about-mobile-menu-toggle"
            className="md:hidden h-10 w-10 rounded-lg flex items-center justify-center text-neutral-700 hover:bg-neutral-100 transition-colors" aria-label="Toggle menu">
            {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
        {mobileOpen && (
          <div className="md:hidden border-t border-neutral-100 bg-white" data-testid="about-mobile-menu">
            <nav className="px-4 py-4 flex flex-col gap-1 text-sm font-medium text-neutral-700">
              <Link to="/" onClick={() => setMobileOpen(false)} className="py-2.5 px-2 rounded-lg hover:bg-neutral-50">Home</Link>
              <Link to="/about" onClick={() => setMobileOpen(false)} className="py-2.5 px-2 rounded-lg hover:bg-neutral-50">About</Link>
              <Link to="/#features" onClick={() => setMobileOpen(false)} className="py-2.5 px-2 rounded-lg hover:bg-neutral-50">Features</Link>
              <Link to="/#pricing" onClick={() => setMobileOpen(false)} className="py-2.5 px-2 rounded-lg hover:bg-neutral-50">Pricing</Link>
              <div className="flex flex-col gap-2 pt-3 mt-2 border-t border-neutral-100">
                <Link to="/login" onClick={() => setMobileOpen(false)}><Button variant="outline" className="w-full rounded-full border-neutral-300">Log in</Button></Link>
                <Link to="/register" onClick={() => setMobileOpen(false)}><Button className="w-full rounded-full bg-[#516670] hover:bg-[#41545d] gap-1.5">Get started <ArrowRight className="h-4 w-4" /></Button></Link>
              </div>
            </nav>
          </div>
        )}
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden" data-testid="about-hero">
        <div className="absolute inset-0 bg-grid-fade [mask-image:radial-gradient(ellipse_at_top,black_20%,transparent_70%)]" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 h-[420px] w-[820px] bg-neutral-300/30 blur-[120px] rounded-full -z-0" />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 pt-16 lg:pt-24 pb-10 text-center">
          <Reveal>
            <div className="flex justify-center"><Label icon={ShieldCheck}>About</Label></div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]">
              The command centre for <span className="text-shimmer">elite support teams</span>.
            </h1>
            <p className="mt-6 text-lg text-neutral-500 leading-relaxed">
              At the highest levels of business, entertainment and private wealth, execution is everything — yet the people supporting the world's most impactful individuals are often left patching together generic tools that fail to understand the complex, interconnected nature of a principal's life.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Intro / system of record */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-10 lg:py-14">
        <Reveal className="rounded-3xl border border-neutral-200 bg-neutral-50/60 p-8 sm:p-12">
          <p className="text-lg sm:text-xl text-neutral-700 leading-relaxed">
            Swishr Central is the definitive answer to this operational friction. A flagship product of <span className="font-semibold text-neutral-900">FGP</span>, the platform operates as a secure, relationship-aware <span className="font-semibold text-neutral-900">system of record</span>. Built explicitly for Executive Assistants, Personal Assistants and Family Offices, it unifies schedules, tasks, documents, renewals and networks into a single, flawless command centre.
          </p>
        </Reveal>
      </section>

      {/* The Alliance */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-12 lg:py-16">
        <Reveal>
          <Label icon={Handshake} tint={ACCENTS[4]}>The Alliance</Label>
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight">FGP × Swishr</h2>
        </Reveal>
        <div className="mt-8 grid lg:grid-cols-2 gap-6">
          <Reveal delay={0.05}>
            <p className="text-neutral-600 leading-relaxed">
              Swishr Central is the result of a powerful strategic partnership between <span className="font-semibold text-neutral-900">FGP</span>, led by international operations expert Fisca Galih Pramanik, and <span className="font-semibold text-neutral-900">Swishr</span> — a premier global ecosystem recognised for elite talent acquisition and cutting-edge business productivity platforms.
            </p>
            <p className="mt-4 text-neutral-600 leading-relaxed">
              While Swishr's recruitment arm, <span className="font-medium text-neutral-800">Swishr Hire</span>, has mastered sourcing and placing high-performing support talent worldwide, they constantly witnessed a glaring roadblock: even the world's best EAs and PAs were limited by fragmented, outdated software.
            </p>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="rounded-3xl bg-[#516670] text-white p-8 relative overflow-hidden h-full">
              <div className="absolute inset-0 bg-grid-fade opacity-[0.15]" />
              <div className="absolute -top-8 -right-8 h-44 w-44 rounded-full bg-[#2D74B4]/25 blur-3xl" />
              <div className="relative">
                <Sparkles className="h-6 w-6 text-[#7FB0DA] mb-4" />
                <p className="text-lg leading-relaxed text-neutral-200">
                  Recognising that <span className="text-white font-semibold">elite talent requires elite infrastructure</span>, FGP partnered with Swishr to co-engineer the solution — drawing directly from Swishr's extensive pedigree in enterprise productivity software.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Ecosystem */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-12 lg:py-16">
        <Reveal className="max-w-2xl">
          <Label icon={Building2} tint={ACCENTS[1]}>Ecosystem</Label>
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight">Part of the Swishr software ecosystem</h2>
          <p className="mt-4 text-neutral-500">Swishr Central is backed by the same engineering and product philosophy behind Swishr's market-leading SaaS suite.</p>
        </Reveal>
        <div className="mt-8 grid md:grid-cols-3 gap-5">
          {ecosystem.map((e, i) => (
            <Reveal key={e.name} delay={i * 0.08}>
              <a href={e.url} target="_blank" rel="noopener noreferrer" data-testid={`ecosystem-${i}`}
                className="group block h-full rounded-2xl border border-neutral-200 bg-white p-7 hover:shadow-xl hover:shadow-neutral-900/5 hover:-translate-y-1 transition-all duration-300">
                <div className="rounded-xl overflow-hidden mb-5 aspect-[16/10] border border-neutral-200 bg-neutral-100">
                  <img src={e.thumb} alt={`Swishr ${e.name} website`} loading="lazy" className="h-full w-full object-cover object-top group-hover:scale-[1.04] transition-transform duration-500" />
                </div>
                <div className="flex items-center justify-between mb-1">
                  <h3 className="font-heading text-lg font-semibold">Swishr <span className="text-[#FF4A1C]">{e.name}</span></h3>
                  <ArrowUpRight className="h-4 w-4 text-neutral-300 group-hover:text-[#FF4A1C] transition-colors" />
                </div>
                <p className="text-xs text-neutral-400">{e.url.replace("https://", "")}</p>
                <p className="text-sm text-neutral-500 leading-relaxed mt-2">{e.desc}</p>
              </a>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.16}>
          <p className="mt-6 text-neutral-600 leading-relaxed">
            By pairing FGP's experience in high-stakes private-office management with Swishr's proven track record in high-utility software engineering, the two organisations built Swishr Central — the ultimate operational ecosystem.
          </p>
        </Reveal>
      </section>

      {/* Vision + quote */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 pb-12">
        <Reveal className="max-w-3xl">
          <Label icon={Sparkles} tint={ACCENTS[2]}>The vision</Label>
          <p className="text-lg text-neutral-700 leading-relaxed">
            Fisca founded FGP and designed Swishr Central because she realised that whether you are managing an international business empire, a world-class theatrical production or a multi-property private network, generic software leaves teams blind. Swishr Central provides the secure, intuitive control room that acts as the single source of truth — allowing elite support teams to command their principal's entire world from a single screen.
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <figure className="mt-10 rounded-3xl bg-[#516670] text-white p-8 sm:p-12 relative overflow-hidden">
            <div className="absolute inset-0 bg-grid-fade opacity-[0.15]" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[260px] w-[520px] bg-[#2D74B4]/20 blur-[110px] rounded-full" />
            <div className="relative">
              <Quote className="h-8 w-8 text-[#7FB0DA] mb-4" />
              <blockquote className="font-heading text-xl sm:text-2xl font-medium leading-relaxed max-w-2xl">
                “Finding the right support talent is only the first step. With Swishr Central, we are giving those elite teams the digital headquarters they need to turn everyday operational chaos into absolute harmony.”
              </blockquote>
              <figcaption className="mt-5 flex items-center gap-3">
                <img src={FOUNDER_URL} alt="Fisca Galih Pramanik" className="h-10 w-10 rounded-full object-cover ring-2 ring-white/20" />
                <div><p className="text-sm font-semibold">Fisca Galih Pramanik</p><p className="text-xs text-neutral-400">Founder, FGP</p></div>
              </figcaption>
            </div>
          </figure>
        </Reveal>
      </section>

      {/* CTA */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 pb-20">
        <Reveal className="rounded-[2rem] border border-neutral-200 bg-neutral-50/60 px-8 py-14 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight max-w-xl mx-auto">Give your team the digital headquarters they deserve.</h2>
          <p className="mt-4 text-neutral-500 max-w-lg mx-auto">Step inside Swishr Central — people, documents, renewals, calendar, AI brief and reports, in one trusted place.</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/login"><Button size="lg" data-testid="about-cta" className="rounded-full bg-[#516670] hover:bg-[#41545d] gap-2 w-full sm:w-auto px-7 transition-transform hover:scale-[1.03]">Enter the demo <ArrowRight className="h-4 w-4" /></Button></Link>
            <Link to="/"><Button size="lg" variant="outline" className="rounded-full w-full sm:w-auto border-neutral-300 px-7 hover:bg-white">Back to home</Button></Link>
          </div>
        </Reveal>
      </section>

      {/* Footer */}
      <footer className="border-t border-neutral-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <Link to="/"><Logo className="h-28 sm:h-40" /></Link>
          <p className="text-sm text-neutral-400 order-last sm:order-none">© 2026 Swishr Digital Ltd. All rights reserved.</p>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
            <Link to="/about" className="text-sm text-neutral-500 hover:text-neutral-900 transition-colors">About</Link>
            <Link to="/security" className="text-sm text-neutral-500 hover:text-neutral-900 transition-colors">Security</Link>
            <Link to="/privacy" className="text-sm text-neutral-500 hover:text-neutral-900 transition-colors">Privacy Policy</Link>
            <Link to="/refunds" className="text-sm text-neutral-500 hover:text-neutral-900 transition-colors">Refund Policy</Link>
            <Link to="/terms" className="text-sm text-neutral-500 hover:text-neutral-900 transition-colors">Terms &amp; Conditions</Link>
            <button onClick={openCookieSettings} className="text-sm text-neutral-500 hover:text-neutral-900 transition-colors" data-testid="footer-cookie-settings">Cookie settings</button>
          </div>
        </div>
      </footer>
    </div>
  );
}
